/**
 * External Navigation Utilities
 * 
 * Handles opening directions (and other external links) reliably across:
 * - Iframe contexts (Lovable preview)
 * - Desktop browsers
 * - iOS Safari
 * - Despia native iOS shell (GroundediOS UA)
 */
import { isNativeApp } from "./platform";



export interface DirectionsParams {
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  label?: string;
}

/**
 * Detect if we're running inside an iframe (e.g. Lovable preview)
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin iframes throw, which also means we're in an iframe
    return true;
  }
}

/**
 * Detect iOS (iPhone, iPad, iPod)
 * Includes iPadOS detection via platform check
 */
export function isIOS(): boolean {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  
  // Direct iOS check
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  
  // iPadOS 13+ reports as MacIntel but has touch
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  
  return false;
}

/**
 * Build a directions URL based on context:
 * - In iframe → always Google Maps (avoids ERR_BLOCKED_BY_RESPONSE)
 * - On iOS (not iframe) → Apple Maps for native experience
 * - Otherwise → Google Maps
 */
export function buildDirectionsUrl(params: DirectionsParams): string | null {
  const { latitude, longitude, address, label } = params;
  
  const hasCoords = latitude != null && longitude != null;
  const hasAddress = !!address?.trim();
  
  if (!hasCoords && !hasAddress) {
    return null;
  }
  
  // In iframe: always use Google Maps to avoid Apple Maps blocking
  if (isInIframe()) {
    return _buildGoogleMapsUrl(params);
  }
  
  // On iOS (or Despia native shell) outside iframe: prefer Apple Maps
  if (isNativeApp() || isIOS()) {
    return _buildAppleMapsUrl(params);
  }

  
  // Default: Google Maps
  return _buildGoogleMapsUrl(params);
}

function _buildGoogleMapsUrl(params: DirectionsParams): string {
  const { latitude, longitude, address } = params;
  
  if (latitude != null && longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  }
  
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address!)}`;
}

function _buildAppleMapsUrl(params: DirectionsParams): string {
  const { latitude, longitude, address, label } = params;
  const host = isNativeApp() ? "maps://" : "https://maps.apple.com/";

  if (latitude != null && longitude != null) {
    const labelParam = label ? `&q=${encodeURIComponent(label)}` : '';
    return `${host}?ll=${latitude},${longitude}&daddr=${latitude},${longitude}${labelParam}`;
  }

  return `${host}?daddr=${encodeURIComponent(address!)}`;
}

export function buildGoogleMapsUrl(lat: number | null | undefined, lng: number | null | undefined, address?: string): string {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`;
}

export function buildAppleMapsUrl(lat: number | null | undefined, lng: number | null | undefined, name?: string, address?: string): string {
  const host = isNativeApp() ? "maps://" : "https://maps.apple.com/";
  if (lat != null && lng != null) {
    return `${host}?daddr=${lat},${lng}&q=${encodeURIComponent(name || '')}`;
  }
  return `${host}?q=${encodeURIComponent(address || '')}`;
}

/**
 * Open an external URL reliably across browsers and iframe contexts
 * Uses multiple strategies to bypass iframe blocking:
 * 1. Anchor with no-referrer policy (strips iframe context)
 * 2. window.open fallback
 * 3. Clipboard fallback with notification
 */
export function openExternal(url: string, showFallbackToast?: () => void): void {
  // Custom-scheme URLs (e.g. maps://, com.despia.grounded://) need a direct
  // navigation assignment to trigger WKWebView's app handoff. Anchor clicks
  // with target="_blank" don't reliably dispatch non-http schemes in the shell.
  if (!/^https?:\/\//i.test(url)) {
    window.location.href = url;
    return;
  }

  try {
    // Create a temporary anchor element with no-referrer to strip iframe context
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.setAttribute('referrerpolicy', 'no-referrer');
    
    // Append to body, click, and remove
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } catch {
    // Fallback to window.open
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Ultimate fallback: copy to clipboard
      copyToClipboardFallback(url, showFallbackToast);
    }
  }
}

/**
 * Copy URL to clipboard as ultimate fallback when external links are blocked
 */
async function copyToClipboardFallback(url: string, showToast?: () => void): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    showToast?.();
  } catch {
    // If clipboard also fails, at least log the URL
    console.log('Directions URL:', url);
  }
}
